import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../Api';
import { Store } from '../Store';
import { getError, otpProviderLabel } from '../utils';
import '../assets/ops.css';

// Customer side of the delivery flow: what is still owed to them, and a way to
// ask for part of it. They confirm the request with an OTP (when that gate is
// on), then collect at the counter.

export default function MyDeliveryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [data, setData] = useState(null);
  const [requests, setRequests] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestFor, setRequestFor] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${userInfo?.token}` }),
    [userInfo]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deliverable, mine] = await Promise.all([
        axios.get(`${api}/api/delivery/deliverable`, { headers }),
        axios.get(`${api}/api/delivery/my-requests`, { headers }),
      ]);
      setData(deliverable.data);
      setRequests(mine.data);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const cancel = async (request) => {
    if (!window.confirm(`Cancel request ${request.code}?`)) return;
    try {
      const { data: result } = await axios.post(
        `${api}/api/delivery/request/${request._id}/cancel`,
        {},
        { headers }
      );
      toast.success(result.message);
      load();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const config = data?.config;
  const lines = (data?.lines || []).filter((l) => l.remaining > 0 || l.pending > 0);
  const totals = (data?.lines || []).reduce(
    (acc, l) => ({
      remaining: acc.remaining + l.remaining,
      pending: acc.pending + l.pending,
      delivered: acc.delivered + l.delivered,
    }),
    { remaining: 0, pending: 0, delivered: 0 }
  );

  return (
    <div className="ops">
      <Helmet><title>My Deliveries</title></Helmet>

      <div className="ops-head">
        <div>
          <h2>My Deliveries</h2>
          <p>Ask to collect what you have ordered, then pick it up at the counter.</p>
        </div>
        <button className="ops-btn" onClick={load} disabled={loading}>
          <i className="fa fa-refresh" /> Refresh
        </button>
      </div>

      <div className="ops-stats">
        <div className="ops-stat">
          <div className="label">Ready to request</div>
          <div className="value">{totals.remaining}</div>
          <div className="hint">units you can ask for</div>
        </div>
        <div className="ops-stat">
          <div className="label">Waiting to collect</div>
          <div className="value" style={{ color: totals.pending ? 'var(--ops-amber)' : undefined }}>
            {totals.pending}
          </div>
          <div className="hint">requested, not collected</div>
        </div>
        <div className="ops-stat">
          <div className="label">Collected</div>
          <div className="value">{totals.delivered}</div>
          <div className="hint">units received</div>
        </div>
      </div>

      {config && !config.requestEnabled && (
        <div className="ops-note warn" style={{ marginBottom: 16 }}>
          <i className="fa fa-pause-circle" />
          <span>Delivery requests are paused right now. Please check back later.</span>
        </div>
      )}

      <div className="ops-card">
        <h3>Your ordered items</h3>
        <p className="sub">
          {config?.requireOrderApproved
            ? 'Only approved orders can be collected.'
            : 'All your orders are listed here.'}
        </p>

        {loading && !data ? (
          <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading…</div>
        ) : !lines.length ? (
          <div className="ops-empty">
            <i className="fa fa-shopping-bag" />
            Nothing waiting to be collected.
          </div>
        ) : (
          <div className="ops-rows">
            {lines.map((line) => (
              <div key={`${line.orderId}-${line.itemIndex}`} className="ops-row" style={{ cursor: 'default' }}>
                <img className="ops-thumb" src={line.image} alt="" />
                <div className="main">
                  <strong>{line.name}</strong>
                  <span>
                    Ordered {line.quantity} · collected {line.delivered}
                    {line.pending > 0 && ` · ${line.pending} waiting at the counter`}
                    {line.legacy && ' · collect at the counter'}
                  </span>
                </div>
                <div className="side">
                  {line.legacy ? (
                    // Placed before the new delivery process began, so it is
                    // handed over at the counter instead of requested here.
                    <span className="ops-badge amber" title="This order was placed before the new delivery process started.">
                      Counter pickup
                    </span>
                  ) : line.remaining > 0 ? (
                    <button
                      className="ops-btn sm primary"
                      disabled={!config?.requestEnabled}
                      onClick={() => setRequestFor(line)}
                    >
                      Request {line.remaining === 1 ? '1' : `up to ${line.remaining}`}
                    </button>
                  ) : (
                    <span className="ops-badge amber">Awaiting pickup</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ops-card">
        <h3>Your requests</h3>
        <p className="sub">Show the request code at the counter when you collect.</p>

        {!requests?.rows?.length ? (
          <div className="ops-empty"><i className="fa fa-inbox" />You have not requested any delivery yet.</div>
        ) : (
          <div className="ops-table-wrap">
            <table className="ops-table" style={{ minWidth: 620 }}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th>Status</th>
                  <th className="ops-hide-sm">Requested</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {requests.rows.map((request) => (
                  <tr key={request._id}>
                    <td className="ops-mono">{request.code}</td>
                    <td>{request.productName}</td>
                    <td className="num"><strong>{request.quantity}</strong></td>
                    <td><RequestStatus request={request} /></td>
                    <td className="ops-hide-sm" style={{ fontSize: 12, color: 'var(--ops-muted)' }}>
                      {new Date(request.createdAt).toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {['pending', 'in_progress'].includes(request.status) && config?.allowUserCancel && (
                        <button className="ops-btn sm danger" onClick={() => cancel(request)}>Cancel</button>
                      )}
                      {request.status === 'completed' && request.deliveredSerials?.length > 0 && (
                        <span className="ops-mono" style={{ fontSize: 11, color: 'var(--ops-muted)' }}>
                          {request.deliveredSerials.join(', ')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {requestFor && (
        <RequestModal
          line={requestFor}
          config={config}
          headers={headers}
          onClose={() => setRequestFor(null)}
          onDone={() => { setRequestFor(null); load(); }}
        />
      )}
    </div>
  );
}

function RequestStatus({ request }) {
  const map = {
    pending: ['grey', 'Waiting'],
    in_progress: ['blue', 'At the counter'],
    completed: ['green', 'Collected'],
    rejected: ['red', 'Rejected'],
    cancelled: ['grey', 'Cancelled'],
  };
  const [tone, label] = map[request.status] || ['grey', request.status];
  return (
    <span className={`ops-badge ${tone}`} title={request.rejectReason || ''}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Request dialog — quantity, then OTP when that gate is on
// ---------------------------------------------------------------------------
function RequestModal({ line, config, headers, onClose, onDone }) {
  const maxAllowed = config?.maxRequestQuantity > 0
    ? Math.min(config.maxRequestQuantity, line.remaining)
    : line.remaining;
  const forcedFull = config && !config.allowPartial;

  const [quantity, setQuantity] = useState(forcedFull ? line.remaining : 1);
  const [stage, setStage] = useState('quantity'); // quantity | otp
  const [otp, setOtp] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCountdown = (seconds) => {
    setCountdown(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${api}/api/delivery/request`,
        { orderId: line.orderId, itemIndex: line.itemIndex, quantity: Number(quantity) },
        { headers }
      );
      if (data.otpRequired) {
        setOtp(data);
        setStage('otp');
        startCountdown(data.resendAfterSeconds || 60);
        toast.info(`Code sent to ${data.maskedPhone}.`);
      } else {
        toast.success(data.message);
        onDone();
      }
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${api}/api/delivery/request/verify`,
        { actionToken: otp.actionToken, code: code.trim() },
        { headers }
      );
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (countdown > 0) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${api}/api/delivery/request/resend`,
        { actionToken: otp.actionToken },
        { headers }
      );
      startCountdown(data.resendAfterSeconds || 60);
      toast.success('A new code was sent.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ops-overlay" onClick={onClose}>
      <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ops-modal-head">
          <div>
            <h4>{stage === 'otp' ? 'Confirm your request' : 'Request delivery'}</h4>
            <p className="sub">{line.name}</p>
          </div>
          <button className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        {stage === 'quantity' ? (
          <form onSubmit={submit}>
            <div className="ops-modal-body">
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                <img className="ops-thumb" style={{ width: 54, height: 54 }} src={line.image} alt="" />
                <div>
                  <strong style={{ display: 'block' }}>{line.name}</strong>
                  <span style={{ fontSize: 12.5, color: 'var(--ops-muted)' }}>
                    {line.remaining} of {line.quantity} left to collect
                  </span>
                </div>
              </div>

              <div className="ops-field">
                <label className="ops-label">How many do you want to collect?</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="ops-btn"
                    disabled={forcedFull || quantity <= 1}
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  >
                    <i className="fa fa-minus" />
                  </button>
                  <input
                    className="ops-input ops-scan-input"
                    type="number"
                    min="1"
                    max={maxAllowed}
                    disabled={forcedFull}
                    value={quantity}
                    onChange={(e) =>
                      setQuantity(Math.min(Math.max(Number(e.target.value) || 1, 1), maxAllowed))
                    }
                  />
                  <button
                    type="button"
                    className="ops-btn"
                    disabled={forcedFull || quantity >= maxAllowed}
                    onClick={() => setQuantity((q) => Math.min(maxAllowed, q + 1))}
                  >
                    <i className="fa fa-plus" />
                  </button>
                </div>
                {forcedFull && (
                  <p style={{ fontSize: 12, color: 'var(--ops-muted)', margin: '8px 0 0' }}>
                    Partial collection is switched off, so this request covers all {line.remaining}.
                  </p>
                )}
                {!forcedFull && config?.maxRequestQuantity > 0 && (
                  <p style={{ fontSize: 12, color: 'var(--ops-muted)', margin: '8px 0 0' }}>
                    Up to {config.maxRequestQuantity} per request.
                  </p>
                )}
              </div>

              <div className="ops-note info">
                <i className="fa fa-info-circle" />
                <span>
                  {config?.requestOtpEnabled
                    ? 'We will send a code to your phone to confirm this request.'
                    : 'Bring your request code to the counter to collect.'}
                </span>
              </div>
            </div>
            <div className="ops-modal-foot">
              <button type="button" className="ops-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="ops-btn primary" disabled={busy || quantity < 1}>
                {busy ? 'Please wait…' : `Request ${quantity} unit${quantity === 1 ? '' : 's'}`}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={verify}>
            <div className="ops-modal-body">
              <p style={{ fontSize: 13.5, marginBottom: 14 }}>
                We sent a 6-digit code to <strong>{otp.maskedPhone}</strong>
                {otp.sentVia?.length ? ` via ${otp.sentVia.map(otpProviderLabel).join(' & ')}` : ''}.
              </p>
              <div className="ops-field">
                <label className="ops-label">Enter the code</label>
                <input
                  className="ops-input ops-scan-input"
                  inputMode="numeric"
                  autoFocus
                  maxLength={8}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                />
              </div>
              <button
                type="button"
                className="ops-btn ghost sm"
                onClick={resend}
                disabled={countdown > 0 || busy}
              >
                {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
              </button>
            </div>
            <div className="ops-modal-foot">
              <button type="button" className="ops-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="ops-btn primary" disabled={busy || code.length < 4}>
                {busy ? 'Checking…' : 'Confirm request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
